/**
 * @fileoverview ChatDrawerContext
 *
 * Provides open/closed state, tab selection, and per-tab unread counts for
 * the Stable Talk drawer. Mounted once at AppShell level so state persists
 * across page navigation.
 *
 * Unread counts are tracked per tab (Friends, Teams, Requests).
 * Lounge intentionally has no badge.
 */

import { createContext, useContext, useState } from 'react';

const ChatDrawerContext = createContext(null);

export function ChatDrawerProvider({ children }) {
  const [isOpen,    setIsOpen]    = useState(false);
  const [activeTab, setActiveTab] = useState('lounge');

  const [friendsUnread,  setFriendsUnread]  = useState(0);
  const [teamsUnread,    setTeamsUnread]    = useState(0);
  const [requestsUnread, setRequestsUnread] = useState(0);

  // Suppresses the floating chat FAB (e.g. while the Stable Slip modal is
  // open, so there's never a floating button fighting for the same corner).
  // Uses a counter, not a boolean, so multiple overlays can suppress it
  // independently without one closing early and re-showing the FAB.
  const [suppressCount, setSuppressCount] = useState(0);
  const isFabSuppressed = suppressCount > 0;
  const suppressFab   = () => setSuppressCount(c => c + 1);
  const unsuppressFab = () => setSuppressCount(c => Math.max(0, c - 1));

  const totalUnread = friendsUnread + teamsUnread + requestsUnread;

  const toggle = () => setIsOpen(prev => !prev);
  const open   = () => setIsOpen(true);
  const close  = () => setIsOpen(false);

  function clearTab(tab) {
    if (tab === 'friends')  setFriendsUnread(0);
    if (tab === 'teams')    setTeamsUnread(0);
    if (tab === 'requests') setRequestsUnread(0);
  }

  return (
    <ChatDrawerContext.Provider value={{
      isOpen, toggle, open, close,
      activeTab, setActiveTab,
      friendsUnread,  setFriendsUnread,
      teamsUnread,    setTeamsUnread,
      requestsUnread, setRequestsUnread,
      totalUnread,
      clearTab,
      isFabSuppressed, suppressFab, unsuppressFab,
    }}>
      {children}
    </ChatDrawerContext.Provider>
  );
}

export function useChatDrawer() {
  const ctx = useContext(ChatDrawerContext);
  if (!ctx) throw new Error('useChatDrawer must be used within ChatDrawerProvider');
  return ctx;
}
